import React from 'react';
import { useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';

import { BodyText, theme, useResponsive } from '@sg/garnish';
import { AllergenProperty } from '@sg/graphql-schema';
import { AllergenTypes, useDietaryRestrictions } from '@sg/shared-fe-features-menu';

import { HomeProductCardSize } from '../HomeProductCard/HomeProductCard.types';
import { HomeProductNutritionData } from '../HomeProductNutritionData';

// ────────────────────────────────────────────────────────────────────────────

export const HomeProductInfo = (props: HomeProductInfoProps) => {
  const {
    size = 'regular',
    dietaryPreferences,
    allergenProperties,
    calories,
    calorieOverrideText,
    cost,
  } = props;

  const getRestrictionNotice = useDietaryRestrictions({ restrictions: dietaryPreferences });
  const restrictionNotice = getRestrictionNotice({ allergenProperties });

  if (size === 'large') {
    return cost ? <Cost {...props} /> : null;
  }

  return (
    <>
      {restrictionNotice ? <RestrictionNotice restrictionNotice={restrictionNotice} /> : null}

      {hasNutritionData(props) ? <HomeProductNutritionData {...props} /> : null}

      {calories && calorieOverrideText ? <CaloriesOverride {...props} /> : null}

      {cost ? <Cost {...props} /> : null}
    </>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const RestrictionNotice = (props: RestrictionNoticeProps) => {
  const { restrictionNotice } = props;

  return (
    <BodyText sizeMatch={['12']} style={styles.restrictionNoticeText}>
      {restrictionNotice}
    </BodyText>
  );
};

const CaloriesOverride = (props: HomeProductInfoProps) => {
  const { calorieOverrideText } = props;
  const { match } = useResponsive();

  return (
    <BodyText
      sizeMatch={['12']}
      style={match([styles.calorieOverrideTextXS, styles.calorieOverrideTextSM])}
    >
      {calorieOverrideText}
    </BodyText>
  );
};

const Cost = (props: HomeProductInfoProps) => {
  const { cost = 0 } = props;

  const { formatNumber } = useIntl();
  const { match } = useResponsive();

  return (
    <BodyText
      sizeMatch={['12']}
      style={match([styles.costXS, styles.costSM])}
      bold={match([false, true])}
    >
      {formatNumber(cost / 100, { style: 'currency', currency: 'USD' })}
    </BodyText>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const hasNutritionData = (props: HomeProductInfoProps) => {
  return (
    props.calories ||
    props.totalFatG ||
    props.totalCarbsG ||
    props.dietaryFiberG ||
    props.sugarsG ||
    props.proteinG
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  costXS: {
    color: theme.colors.CHARCOAL,
  },
  costSM: {
    color: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
  calorieOverrideTextXS: {
    color: theme.colors.CHARCOAL,
  },
  calorieOverrideTextSM: {
    alignSelf: 'flex-start',
    color: theme.colors.CHARCOAL,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderRadius: theme.radius.large,
    paddingVertical: theme.spacing['1'],
    paddingHorizontal: theme.spacing['2'],
  },
  restrictionNoticeText: {
    color: theme.colors.CAUTION,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeProductInfoProps = {
  size?: HomeProductCardSize;
  dietaryPreferences: AllergenTypes;
  allergenProperties: readonly AllergenProperty[];
  calorieOverrideText?: string;
  cost?: number;
  calories?: number;
  totalFatG?: number;
  totalCarbsG?: number;
  dietaryFiberG?: number;
  sugarsG?: number;
  proteinG?: number;
};

type RestrictionNoticeProps = {
  restrictionNotice: string;
};
